import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, withCtx as _withCtx, mergeProps as _mergeProps, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import RichTextField from '@/components/common/RichTextField.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import MediaList from '@/components/media/MediaList.vue';
import { MediaPurpose } from '@/models/media.model';
import {
  Municipality,
  nameRules,
  postalCodeRules,
} from '@/models/municipality.model';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import {
  MunicipalitiesActions,
  MunicipalityStoreState,
} from '@/stores/municipalities.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';


export default /*@__PURE__*/_defineComponent({
  __name: 'MunicipalityForm',
  props: {
  data: {
    type: Object as PropType<Municipality>,
  },
},
  setup(__props) {

const store = useStore<ShiftCMSState & { ms: MunicipalityStoreState }>();

const events = useEvents();

const props = __props;

const valid = ref<boolean>(false);

const municipality = ref<Municipality>({
  id: NEW_ID,
  name: '',
  description: '',
  postalCodes: [],
  centerCoordinates: { lat: 0, lng: 0 },
  media: [],
  communities: [],
  impactRelevant: true,
});

const action = computed<string>(() =>
  municipality.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveMunicipality = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (municipality.value.id === NEW_ID) {
      success = await store.dispatch(
        MunicipalitiesActions.AddMunicipality,
        municipality.value,
      );
    } else {
      success = await store.dispatch(
        MunicipalitiesActions.UpdateMunicipality,
        municipality.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved Municipality',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(MunicipalitiesActions.GetMunicipalities);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save Municipality',
      type: 'error',
    });
  }
};

const updateLanguage = async () => {
  if (municipality.value.id === NEW_ID) {
    Object.assign(municipality, { title: '' });
  } else {
    const cat = await store.dispatch(
      MunicipalitiesActions.GetMunicipality,
      municipality.value.id,
    );

    if (cat) {
      Object.assign(municipality.value, { ...cat });
    }
  }
};

const postalCodeDialog = ref<boolean>(false);
const postalCodes = ref<string>('');

const parseValue = () => {
  const codes = postalCodes.value
    .replace(/,/g, ' ')
    .replace(/\s\s/g, ' ')
    .split(' ');
  municipality.value.postalCodes = codes;
};

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  if (props.data) {
    Object.assign(municipality.value, {
      ...props.data,
    });
  }
});

const removeLabel = async () => {
  if (
    municipality.value &&
    municipality.value.id !== NEW_ID &&
    confirm('Are you absolutely sure you want to delete this "Municipality"?')
  ) {
    store.dispatch(
      MunicipalitiesActions.DeleteMunicipality,
      municipality.value.id,
    );

    store.commit(Mutations.HideDrawer);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_checkbox = _resolveComponent("v-checkbox")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_combobox = _resolveComponent("v-combobox")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createBlock(_component_v_form, {
    modelValue: valid.value,
    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((valid).value = $event)),
    onSubmit: _withModifiers(saveMunicipality, ["prevent"])
  }, {
    default: _withCtx(() => [
      _createVNode(_component_v_container, null, {
        default: _withCtx(() => [
          _createElementVNode("h2", null, [
            _createTextVNode(_toDisplayString(_ctx.$t(action.value)) + " a municipality ", 1),
            _createVNode(CurrentLanguage)
          ]),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, {
                class: "mx-auto",
                cols: "7"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    modelValue: municipality.value.name,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((municipality.value.name) = $event)),
                    rules: _unref(nameRules),
                    label: "Name",
                    hint: "Not used on municipality page. Use a top level header in the description",
                    required: ""
                  }, null, 8, ["modelValue", "rules"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_checkbox, {
                    modelValue: municipality.value.impactRelevant,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((municipality.value.impactRelevant) = $event)),
                    label: "Show on impact site"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(RichTextField, {
            label: "Description",
            height: 200,
            modelValue: municipality.value.description,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((municipality.value.description) = $event)),
            media: municipality.value.media
          }, null, 8, ["modelValue", "media"]),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, { cols: "11" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_combobox, {
                    label: "Postal Codes",
                    modelValue: municipality.value.postalCodes,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((municipality.value.postalCodes) = $event)),
                    multiple: "",
                    chips: "",
                    hint: "Just the numbers please",
                    clearable: "",
                    rules: _unref(postalCodeRules)
                  }, null, 8, ["modelValue", "rules"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_dialog, {
                    modelValue: postalCodeDialog.value,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((postalCodeDialog).value = $event)),
                    width: "600"
                  }, {
                    activator: _withCtx(({ props: activatorProps }) => [
                      _createVNode(_component_v_btn, _mergeProps({
                        style: {"position":"relative","top":"8px"},
                        size: "32",
                        icon: "",
                        color: "option"
                      }, activatorProps), {
                        default: _withCtx(() => [
                          _createVNode(_component_v_icon, { icon: "mdi-content-paste" })
                        ]),
                        _: 2
                      }, 1040)
                    ]),
                    default: _withCtx(({ isActive }) => [
                      _createVNode(_component_v_card, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_card_text, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                label: "Paste your list of postal codes here",
                                modelValue: postalCodes.value,
                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((postalCodes).value = $event))
                              }, null, 8, ["modelValue"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_card_actions, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_spacer),
                              _createVNode(_component_v_btn, {
                                icon: "",
                                onClick: ($event: any) => {
                      isActive.value = false;
                      parseValue();
                    },
                                color: "info"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_icon, { icon: "mdi-close" })
                                ]),
                                _: 2
                              }, 1032, ["onClick"])
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 1
                  }, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    label: "Latitude",
                    hint: "Used to find near communities",
                    modelValue: municipality.value.centerCoordinates.lat,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((municipality.value.centerCoordinates.lat) = $event)),
                    modelModifiers: { number: true }
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_text_field, {
                    label: "Longitude",
                    hint: "Used to find near communities",
                    modelValue: municipality.value.centerCoordinates.lng,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((municipality.value.centerCoordinates.lng) = $event)),
                    modelModifiers: { number: true }
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }),
          _createVNode(MediaList, {
            modelValue: municipality.value,
            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((municipality).value = $event)),
            required: [_unref(MediaPurpose).BackgroundImage]
          }, null, 8, ["modelValue", "required"]),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, { col: "2" }),
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "mt-2",
                    type: "button",
                    color: "warning",
                    onClick: removeLabel,
                    block: ""
                  }, {
                    default: _withCtx(() => _cache[10] || (_cache[10] = [
                      _createTextVNode("Delete")
                    ])),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "mt-2",
                    type: "reset",
                    block: ""
                  }, {
                    default: _withCtx(() => _cache[11] || (_cache[11] = [
                      _createTextVNode("Reset")
                    ])),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_btn, {
                    class: "mt-2",
                    type: "submit",
                    color: "success",
                    block: ""
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(action.value), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_col, { col: "2" })
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})